// useEditLock — take a lock on a resource while an editor dialog is open.
// Pings every 30s to renew, releases on close/unmount, and surfaces the
// current holder + state so the UI can render a banner (Locked / Lost /
// Held-by-me).
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const PING_MS = 30_000;

export function useEditLock(resourceKey, { enabled = true } = {}) {
  const [state, setState] = useState({ status: "idle", holder: null });
  // status: "idle" | "acquiring" | "held" | "locked" | "lost" | "error"
  const heldRef = useRef(false);
  const key = resourceKey;

  const acquire = async (force = false) => {
    if (!key) return;
    setState(s => ({ ...s, status: "acquiring" }));
    try {
      const r = await api.post(`/edit-locks/${encodeURIComponent(key)}/acquire${force ? "?force=true" : ""}`);
      heldRef.current = true;
      setState({ status: "held", holder: r.data.holder });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.holder) {
        heldRef.current = false;
        setState({ status: "locked", holder: d.holder });
      } else {
        setState({ status: "error", holder: null });
      }
    }
  };

  const release = async () => {
    if (!key || !heldRef.current) return;
    heldRef.current = false;
    try { await api.post(`/edit-locks/${encodeURIComponent(key)}/release`); } catch {}
  };

  useEffect(() => {
    if (!enabled || !key) return;
    let cancelled = false;
    (async () => { if (!cancelled) await acquire(false); })();
    const iv = setInterval(async () => {
      if (!heldRef.current) return;
      try {
        await api.post(`/edit-locks/${encodeURIComponent(key)}/ping`);
      } catch (e) {
        if (e.response?.status === 409) {
          heldRef.current = false;
          const holder = e.response.data?.detail?.holder;
          setState({ status: "lost", holder });
        }
      }
    }, PING_MS);
    return () => {
      cancelled = true;
      clearInterval(iv);
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  return { ...state, takeOver: () => acquire(true), release };
}
